import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { MACHINE_AREAS } from "@/pages/NewDesign";

const FIELDS = [
  { key: "stitch_rate_paisa",    label: "stitchRatePaisa", testId: "settings-stitch-paisa" },
  { key: "margin_percent",       label: "margin",           testId: TID.settingsMargin },
  { key: "thread_price_per_gram",label: "threadPrice",      testId: TID.settingsThread },
  { key: "zari_price_per_gram",  label: "zariPrice",        testId: TID.settingsZari },
  { key: "sequin_price_per_unit",label: "sequinPrice",      testId: TID.settingsSequin },
];

export default function Settings() {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/cost-settings").then((r) => setS(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...Object.fromEntries(FIELDS.map(({ key }) => [key, parseFloat(s[key] || 0)])),
        default_machine_area: s.default_machine_area || "12x400",
      };
      const { data } = await api.put("/cost-settings", payload);
      setS(data);
      toast.success(t("saved"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!s) return <div className="py-16 text-center text-muted-foreground">Loading…</div>;

  const currentArea = MACHINE_AREAS.find((a) => a.v === s.default_machine_area);

  return (
    <div className="space-y-5">
      <div>
        <div className="sq-label">{t("settings")}</div>
        <h1 className="text-3xl font-heading font-bold mt-1 text-secondary">{t("settingsTitle")}</h1>
      </div>

      {/* Machine area */}
      <div className="sq-card p-5 space-y-3">
        <div>
          <h2 className="font-heading font-semibold text-base">{t("defaultMachineArea")}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{t("defaultMachineAreaHint")}</p>
        </div>
        <div>
          <Label className="sq-label">{t("machineArea")}</Label>
          <Select
            value={s.default_machine_area || "12x400"}
            onValueChange={(v) => setS({ ...s, default_machine_area: v })}
          >
            <SelectTrigger className="h-12 mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MACHINE_AREAS.map((a) => (
                <SelectItem key={a.v} value={a.v}>
                  {a.label}{a.heads > 1 ? ` — ${a.heads} ${t("heads")}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {currentArea && currentArea.frame > 0 && (
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
              {t("headsPerMeterHint").replace("{hpm}", (1000 / currentArea.frame).toFixed(1)).replace("{mm}", currentArea.frame)}
            </p>
          )}
        </div>
      </div>

      {/* Cost rates */}
      <div className="sq-card p-5 space-y-4">
        <div>
          <h2 className="font-heading font-semibold text-base">{t("meterPricing")}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{t("meterPricingHint")}</p>
        </div>
        {FIELDS.map(({ key, label, testId }) => (
          <div key={key}>
            <Label className="sq-label">{t(label)}</Label>
            <Input
              data-testid={testId}
              type="number"
              inputMode="decimal"
              step="0.01"
              className="h-12 mt-1.5"
              value={s[key]}
              onChange={(e) => setS({ ...s, [key]: e.target.value })}
            />
          </div>
        ))}
      </div>

      <Button data-testid={TID.settingsSave} onClick={save} disabled={saving}
        className="h-14 w-full text-lg font-semibold sq-tap">
        {saving ? "…" : t("saveSettings")}
      </Button>
    </div>
  );
}
